import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Skeleton } from "@/components/ui/skeleton";
import { memo, useState, useEffect, useRef, CSSProperties } from "react";
import { isLowDataMode } from "@/utils/networkMonitor";
import { useIsMobile } from "@/hooks/use-mobile";
import { DebugTooltip } from "@/components/ui/debug-tooltip";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { analyzeImage, getOptimalImageStyle } from "@/utils/imageAnalysis";

interface ServiceImageLoaderProps {
  src: string;
  alt: string;
  aspectRatio?: number;
  width?: number;
  height?: number;
  className?: string;
  sizes?: string;
  priority?: boolean;
  showPlaceholder?: boolean;
  fallbackSrc?: string;
  onLoad?: () => void;
  onError?: () => void;
  variants?: Record<string, string>;
  placeholder?: string;
  debugInfo?: string; // Debug info property - enforcing string type
}

// Define the allowed types for HTML attributes to avoid type errors
type ObjectFitValue = "contain" | "cover" | "fill" | "none" | "scale-down";
type LoadingValue = "eager" | "lazy"; 
type DecodingValue = "sync" | "async" | "auto";
type FetchPriorityValue = "high" | "auto" | "low";

// Define a proper interface for image styles
interface ImageStyles extends CSSProperties {
  objectFit: ObjectFitValue;
  width: string;
  height: string;
  transition?: string;
}

export const ServiceImageLoader = memo(({
  src,
  alt,
  aspectRatio = 4/3,
  width = 400,
  height = 300,
  className = "",
  sizes = "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw",
  priority = false,
  showPlaceholder = true,
  fallbackSrc = 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d',
  onLoad,
  onError,
  variants = {},
  placeholder,
  debugInfo
}: ServiceImageLoaderProps) => {
  const isLowData = isLowDataMode();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [currentSrc, setCurrentSrc] = useState<string>(src || fallbackSrc);
  const [errorCount, setErrorCount] = useState(0);
  const [imageStyle, setImageStyle] = useState<CSSProperties>({
    objectFit: 'cover',
    objectPosition: 'center'
  });
  const imgRef = useRef<HTMLImageElement>(null);
  
  // Set fetchpriority attribute after mount
  useEffect(() => {
    if (imgRef.current) {
      imgRef.current.setAttribute('fetchpriority', priority ? 'high' : 'auto');
    }
  }, [priority]);
  
  // Reset error count when src changes
  useEffect(() => {
    setErrorCount(0);
  }, [src]);
  
  // Handle image load success with smart positioning
  const handleImageLoad = () => {
    setImageLoaded(true);
    setErrorCount(0);
    
    // Analyze the image and apply optimal styling
    if (imgRef.current) {
      const optimalStyle = getOptimalImageStyle(imgRef.current);
      setImageStyle({
        objectFit: optimalStyle.objectFit,
        objectPosition: optimalStyle.objectPosition,
        width: '100%',
        height: '100%',
        transition: 'opacity 0.2s ease-in-out',
      });
    }
    
    if (onLoad) onLoad();
  };
  
  // Handle image load error
  const handleImageError = () => {
    console.error(`Image failed to load:`, currentSrc);
    
    // If we're already using the fallback image and that fails too
    if (currentSrc === fallbackSrc) {
      console.log(`Even fallback image failed to load`);
      if (onError) onError();
      return;
    }
    
    // Increment error count and try fallback
    setErrorCount(prev => {
      if (prev >= 2 && fallbackSrc) {
        console.log(`Too many errors (${prev + 1}), switching to fallback:`, fallbackSrc);
        setCurrentSrc(fallbackSrc);
      }
      return prev + 1;
    });
    
    // Call error callback if provided
    if (errorCount >= 2 && onError) {
      onError();
    }
  };
  
  // Enhanced image element with smart styling
  const imageElement = (
    <img
      ref={imgRef}
      src={currentSrc}
      alt={alt}
      width={width}
      height={height}
      sizes={sizes}
      onLoad={handleImageLoad}
      onError={handleImageError}
      className={`w-full h-full ${className} ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
      loading={priority ? "eager" : "lazy"}
      decoding={priority ? "sync" : "async"}
      style={imageStyle}
    />
  );
  
  // Enhanced safety for debug content rendering
  const renderDebugContent = () => {
    // Safety check for debugInfo
    const safeDebugInfo = typeof debugInfo === 'string' ? debugInfo : 
                         (debugInfo ? String(debugInfo) : 'No debug information available');
    
    try {
      return (
        <div className="p-2 max-w-[400px] break-all">
          <p className="font-bold">Current URL:</p>
          <p className="text-blue-300 mb-1">{currentSrc}</p>
          {errorCount > 0 && (
            <p className="text-red-400">Load errors: {errorCount}</p>
          )}
          {safeDebugInfo && (
            <div className="mt-1 pt-1 border-t border-gray-600">
              <pre className="whitespace-pre-wrap break-all">{safeDebugInfo}</pre>
            </div>
          )}
        </div>
      );
    } catch (error) {
      console.error('Error rendering debug content:', error);
      return <div className="p-2">Error rendering debug content</div>;
    }
  };

  return (
    <ErrorBoundary componentName="ServiceImageLoader">
      <div 
        className="bg-gray-100 relative overflow-hidden" 
        style={{ 
          aspectRatio: `${aspectRatio}`,
          height: 0,
          paddingBottom: `${(1 / aspectRatio) * 100}%`,
          contain: 'layout',
          width: '100%',
        }}>
        {showPlaceholder && !imageLoaded && (
          <div className="absolute inset-0">
            <Skeleton className="h-full w-full rounded-none" />
          </div>
        )}
        
        <AspectRatio ratio={aspectRatio} className="h-full w-full absolute inset-0">
          {debugInfo ? (
            <DebugTooltip content={renderDebugContent()}>
              {imageElement}
            </DebugTooltip>
          ) : (
            imageElement
          )}
        </AspectRatio>
      </div>
    </ErrorBoundary>
  );
});

ServiceImageLoader.displayName = "ServiceImageLoader";

interface ServiceCardSkeletonProps {
  count?: number;
  aspectRatio?: string | number;
}

export const ServiceCardSkeleton = ({ count = 1, aspectRatio = '4/3' }: ServiceCardSkeletonProps) => {
  const skeletonCount = Array.from({ length: count }, (_, index) => index);
  
  // Convert string aspect ratio like '4/3' to a number
  const numericRatio: number = typeof aspectRatio === 'string' 
    ? (() => {
        if (aspectRatio.includes('/')) {
          const [numerator, denominator] = aspectRatio.split('/').map(Number);
          return denominator ? numerator / denominator : 1;
        }
        return parseFloat(aspectRatio) || 1; // Default to 1 if parsing fails
      })()
    : (typeof aspectRatio === 'number' ? aspectRatio : 1); // Ensure it's a number with fallback

  return (
    <>
      {skeletonCount.map((index) => (
        <div key={index} className="relative rounded-md overflow-hidden bg-gray-100">
          <AspectRatio ratio={numericRatio} className="w-full">
            <Skeleton className="h-full w-full" />
          </AspectRatio>
          <div className="absolute bottom-0 left-0 w-full p-4">
            <Skeleton className="h-4 w-3/4 mb-2" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </>
  );
};
